import { onScopeDispose, ref, watch, type Ref } from 'vue';

// Idle session timeout (issue 265): a signed-in session ends after 30 minutes
// without user input, with a one-minute warning first.
//
// Shared here rather than duplicated per app (ADR 0026) because it is one
// suite-wide statement about the session, not a module concern — and because
// the modules are path-routed onto ONE per-tenant origin in production, so the
// apps genuinely share the activity stamp below. A recruiter reading a job ad in
// the ATS tab keeps the Time tab alive; that is the behaviour a per-app timer
// could not produce.
//
// Three deliberate choices:
//
//  1. **Wall clock, not accumulated ticks.** Idleness is `Date.now() - lastActivity`
//     re-derived on every tick. Browsers throttle timers in background tabs (to
//     ~1/minute) and stop them entirely while the machine sleeps, so a timer that
//     counted its own firings would drift long past 30 minutes.
//  2. **Only real user input counts.** Not requests, not polling, not a tab
//     becoming visible. A dashboard that refreshes itself every 30s would
//     otherwise keep an abandoned session alive forever, which is exactly the
//     situation the timeout exists for.
//  3. **The stamp is shared, the timer is per tab.** `localStorage` is the only
//     cross-tab channel that survives a tab being frozen, and every tab reaching
//     the same conclusion from the same stamp needs no leader election.
//
// The client timer is the visible half. The durable half is that the app's
// timeout handler calls its normal logout, which revokes the refresh token
// server-side — so the session cannot be resumed, only the already-issued access
// token remains, and it is minted with a matching 30-minute TTL (user-api).

/** The idle window. A session with no user input for this long ends. */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
/** How long the warning is on screen before the timeout fires. */
export const IDLE_WARNING_MS = 60 * 1000;
/** Shared across every UI on the tenant origin — see the module comment. */
export const IDLE_ACTIVITY_KEY = 'lumi:lastActivityAt';
/**
 * The `?reason=` value a UI carries to the login page after an idle sign-out, so
 * the login can explain what happened instead of looking like a random bounce.
 */
export const IDLE_LOGOUT_REASON = 'inactivity';

// Where the reason waits between "the session ended" and "something redirected".
//
// It cannot simply be an argument to the redirect, because the redirect that
// actually happens is not always the one this composable triggers. Ending the
// session makes the app unauthenticated, and any router guard already in flight
// (a reload, a deep link) then reaches its own `redirectToWorkspace(to.fullPath)`
// and assigns `location.href` a second time — last assignment wins, and it knows
// nothing about inactivity. Observed in the e2e run as a login page with no
// explanation on it.
//
// So the reason is left where every redirect path can find it. sessionStorage,
// not localStorage: this is about THIS tab's sign-out, and it must not follow the
// browser around afterwards.
const SIGN_OUT_REASON_KEY = 'lumi:signedOutReason';

// The marker only has to survive the milliseconds between sign-out and the
// navigation. A window this short means a marker can never be mistaken for the
// explanation of some later, unrelated bounce — no lifecycle bookkeeping needed.
const SIGN_OUT_REASON_TTL_MS = 30_000;

function sessionStore(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Record that this tab's session ended through inactivity. */
export function markSignedOutForInactivity(): void {
  try {
    sessionStore()?.setItem(
      SIGN_OUT_REASON_KEY,
      JSON.stringify({ reason: IDLE_LOGOUT_REASON, at: Date.now() }),
    );
  } catch {
    /* storage unavailable — the caller's explicit `reason` argument still works */
  }
}

/**
 * The pending sign-out reason, or null.
 *
 * Deliberately does NOT consume: several redirects may race to perform the same
 * bounce and the LAST one is the one the browser follows, so every one of them
 * has to see it. The TTL is what keeps it from going stale.
 */
export function pendingSignOutReason(): string | null {
  try {
    const raw = sessionStore()?.getItem(SIGN_OUT_REASON_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { reason?: unknown; at?: unknown };
    if (typeof parsed?.reason !== 'string' || typeof parsed?.at !== 'number') return null;
    if (Date.now() - parsed.at > SIGN_OUT_REASON_TTL_MS) return null;
    return parsed.reason;
  } catch {
    return null;
  }
}

/** Drop any pending marker — a new session starts with no explanation owed. */
export function clearSignOutReason(): void {
  try {
    sessionStore()?.removeItem(SIGN_OUT_REASON_KEY);
  } catch {
    /* as above */
  }
}

// ── The deliberate sign-out (issue 364, B-3) ─────────────────────────────────
//
// Its sibling above answers "why did the session end?". This one answers "may the
// bounce carry a returnPath?", and it exists for exactly the same reason: the
// redirect that actually happens is not always the one the sign-out triggers.
//
// `handleLogout()` ends the session and calls `redirectToWorkspace()` with no
// returnPath — on purpose, because someone who chose to sign out is done with the
// page they were on. But ending the session makes the app unauthenticated, so a
// router guard already in flight reaches its own `redirectToWorkspace(to.fullPath)`
// and assigns `location.href` a second time. Last assignment wins, and it carries
// the page the user just deliberately left, so the next sign-in drops them straight
// back onto it.
//
// So the intent is left where every redirect path can find it, and NOT consumed on
// read: several redirects race to perform the same bounce and the LAST one is the
// one the browser follows, so every one of them has to see it. The short TTL is
// what keeps it from going stale — a window of milliseconds cannot be mistaken for
// the intent behind some later, unrelated bounce.
const DELIBERATE_SIGN_OUT_KEY = 'lumi:deliberateSignOut';
const DELIBERATE_SIGN_OUT_TTL_MS = 30_000;

/** Record that THIS sign-out was the user's own choice, not a bounce. */
export function markDeliberateSignOut(): void {
  try {
    sessionStore()?.setItem(DELIBERATE_SIGN_OUT_KEY, JSON.stringify({ at: Date.now() }));
  } catch {
    /* storage unavailable — the redirect just keeps its returnPath */
  }
}

/**
 * Whether a deliberate sign-out is in flight. Deliberately does NOT consume — see
 * the note above; the TTL is what expires it.
 */
export function pendingDeliberateSignOut(): boolean {
  try {
    const raw = sessionStore()?.getItem(DELIBERATE_SIGN_OUT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { at?: unknown };
    if (typeof parsed?.at !== 'number') return false;
    return Date.now() - parsed.at <= DELIBERATE_SIGN_OUT_TTL_MS;
  } catch {
    return false;
  }
}

/** Drop any pending marker — a new session is nobody's sign-out. */
export function clearDeliberateSignOut(): void {
  try {
    sessionStore()?.removeItem(DELIBERATE_SIGN_OUT_KEY);
  } catch {
    /* as above */
  }
}

// ── Cross-origin session handoff ─────────────────────────────────────────────
//
// The activity stamp below is per ORIGIN, and the suite is not one origin. It is
// one in production for the path-routed modules ({slug}.{domain}/backoffice, /time,
// …), which is the case the stamp was designed for — but NOT across tenants (each
// tenant is its own host), and not at all in local dev, where every UI has its own
// port. Those crossings are made by
// `appendSessionHandoff`: a full-page navigation carrying the live token pair.
//
// On such an arrival the stamp on the origin being ENTERED is a leftover from some
// earlier visit, and it says nothing about the session that just arrived. Judged by
// it, a user who clicked a module a second ago is signed out for inactivity before
// the page finishes booting — and, because the app then redirects to the workspace
// (whose own origin still holds a live session), lands back where they started. That
// is issue 364 F11: the first module pick after a login bounces, the second works
// (the sign-out cleared the stale stamp on its way out).
//
// So an arriving handoff is treated as what it is: a sign-in. It reseeds the stamp
// rather than being judged by it. This does NOT loosen the timeout — the handoff URL
// can only be minted by a source origin whose session was live at click time, i.e.
// the user demonstrably just acted. A session that is genuinely idle still fires, on
// this origin and every other, because nothing but an arrival clears the flag.
//
// One shot, in memory, per page load: an arrival is a single event, and a second
// `start()` in the same document (a re-mount, a second guard) must be judged by the
// stamp like any other.
let handoffArrived = false;

/**
 * Record that a cross-origin session handoff has just landed on this origin, so the
 * idle window is measured from now instead of from whatever this origin last saw.
 *
 * MUST be called before the app mounts — `useIdleLogout`'s watcher is `immediate`,
 * so it evaluates during `app.mount()`, which is synchronous and therefore runs
 * BEFORE any `router.beforeEach` body. A router guard is too late.
 */
export function markSessionHandoffArrival(): void {
  handoffArrived = true;
}

/** Consumed once by `start()`. Exported for tests only. */
export function consumeSessionHandoffArrival(): boolean {
  const arrived = handoffArrived;
  handoffArrived = false;
  return arrived;
}

/**
 * The boot-time one-liner every app that can be handed a session calls from its
 * `main.ts`, before `createApp(...).mount()`.
 *
 * The param names are `appendSessionHandoff`'s (ADR 0024) and are read here, once,
 * rather than re-derived in seven `main.ts` files — the same reason the sign-out key
 * list is shared. Returns whether an arrival was recorded, so a caller can assert it.
 */
export function noteSessionHandoffFromUrl(search?: string): boolean {
  let raw = search;
  if (raw === undefined) {
    if (typeof window === 'undefined') return false;
    raw = window.location?.search ?? '';
  }
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(raw);
  } catch {
    return false;
  }
  // A handoff is the TOKEN pair. `tenantSlug`/`locale`/`theme` ride along on
  // same-origin navigations too (appendSessionHandoff appends the context params
  // unconditionally), and those are not a session arriving.
  if (!params.get('token') && !params.get('refreshToken')) return false;
  markSessionHandoffArrival();
  return true;
}

// How often idleness is re-derived. One second so the countdown in the warning
// dialog is honest; the work per tick is one `Date.now()` and one localStorage
// read.
const TICK_MS = 1000;

// Upper bound on how often activity is published to localStorage. `pointermove`
// fires at screen refresh rate, and a write per event would be thousands of
// serialisations a minute for a value that only has to be accurate to the
// minute. The in-memory copy is always exact; only the cross-tab publication is
// throttled.
const STAMP_PUBLISH_INTERVAL_MS = 5000;

// Capture-phase and passive: capture so a component calling `stopPropagation()`
// (menus and modals do) cannot hide the user's input from us, and passive so we
// never delay the gesture.
//
// `scroll` is deliberately NOT here. It is the one event on this list a script
// can raise: a router's scroll-to-top, a `scrollIntoView()`, an auto-scrolling
// log pane. Counting those would let a page keep its own session alive with
// nobody at the keyboard — precisely what the timeout exists to prevent. Nothing
// is lost, because scrolling by any real means already lands on this list:
// wheel/trackpad fires `wheel`, a scrollbar drag fires `pointerdown`, keys fire
// `keydown`, and touch fires `touchstart`.
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'mousemove',
  'keydown',
  'wheel',
  'touchstart',
] as const;

const LISTENER_OPTIONS = { capture: true, passive: true } as const;

function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

// Every access is guarded: localStorage throws on access in some privacy modes
// and when a browser is configured to block site data. Losing the cross-tab
// stamp degrades this to a per-tab timer — it must never break the app.
function readRawStamp(): string | null {
  if (!hasStorage()) return null;
  try {
    return window.localStorage.getItem(IDLE_ACTIVITY_KEY);
  } catch {
    return null;
  }
}

function writeStamp(at: number): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(IDLE_ACTIVITY_KEY, String(at));
  } catch {
    /* storage unavailable — the in-memory timer still holds */
  }
}

function clearStamp(): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.removeItem(IDLE_ACTIVITY_KEY);
  } catch {
    /* as above */
  }
}

export interface IdleLogoutOptions {
  /**
   * Reactive getter for "there is a session to time out". Must be side-effect
   * free — it is read inside a watcher on every change. Pass something like
   * `() => Boolean(auth.token)`, NOT a store method that logs out on a miss.
   */
  isAuthenticated: () => boolean;
  /** Called once, when the window elapses. The app logs out and redirects. */
  onTimeout: () => void;
  timeoutMs?: number;
  warningMs?: number;
}

export interface IdleLogoutController {
  /** True while the countdown is running — drives the warning dialog. */
  warningVisible: Ref<boolean>;
  /** Whole seconds left before the timeout fires. */
  secondsRemaining: Ref<number>;
  /** The warning's "Stay signed in" action. Also reachable by simply acting. */
  staySignedIn: () => void;
  /** Tear down listeners and the ticker. Called for you on scope disposal. */
  stop: () => void;
}

export function useIdleLogout(options: IdleLogoutOptions): IdleLogoutController {
  const timeoutMs = options.timeoutMs ?? IDLE_TIMEOUT_MS;
  // A warning longer than the window itself would be on screen from the first
  // idle millisecond; clamp rather than surprise the caller.
  const warningMs = Math.min(options.warningMs ?? IDLE_WARNING_MS, timeoutMs);

  const warningVisible = ref(false);
  const secondsRemaining = ref(Math.ceil(timeoutMs / 1000));

  // Exact, per tab. The stamp in localStorage is the throttled, shared copy.
  let localLastActivity = 0;
  let lastPublishedAt = 0;
  let ticker: ReturnType<typeof setInterval> | null = null;
  let running = false;
  let fired = false;

  function readStamp(now: number): number | null {
    const raw = readRawStamp();
    if (raw === null) return null;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return null;
    // A stamp in the future can only mean the clock moved backwards after a
    // write (all tabs on an origin share one clock). Correct it now, or the
    // session would read as "just active" for the whole length of the jump.
    if (value > now) {
      writeStamp(now);
      return now;
    }
    return value;
  }

  function lastActivityAt(now: number): number {
    const stored = readStamp(now);
    // Nothing known at all (first run, storage blocked, stamp cleared) means
    // "active now" — never "idle since the epoch", which would sign out a user
    // who just arrived.
    return Math.max(localLastActivity, stored ?? 0) || now;
  }

  function publishActivity(now: number): void {
    localLastActivity = now;
    // While the warning is up, publish immediately: sibling tabs are showing the
    // same countdown and must drop it at once, and the throttle would otherwise
    // hold the news back for several of the sixty seconds.
    if (warningVisible.value || now - lastPublishedAt >= STAMP_PUBLISH_INTERVAL_MS) {
      writeStamp(now);
      lastPublishedAt = now;
    }
  }

  function fire(): void {
    fired = true;
    warningVisible.value = false;
    secondsRemaining.value = 0;
    teardown();
    // Clear the stamp so the next sign-in in this browser opens a fresh window
    // instead of inheriting the one that just expired and bouncing straight out.
    clearStamp();
    // Leave the reason where ANY redirect path can pick it up, not only the one
    // the app is about to run — see SIGN_OUT_REASON_KEY.
    markSignedOutForInactivity();
    options.onTimeout();
  }

  function evaluate(): void {
    if (!running || fired) return;
    const now = Date.now();
    const remaining = timeoutMs - (now - lastActivityAt(now));
    if (remaining <= 0) {
      fire();
      return;
    }
    warningVisible.value = remaining <= warningMs;
    secondsRemaining.value = Math.ceil(remaining / 1000);
  }

  function onActivity(): void {
    if (!running || fired) return;
    const wasWarning = warningVisible.value;
    publishActivity(Date.now());
    // Re-deriving on every pointer move would put a localStorage read in the
    // input path. Only the warning needs to disappear the instant the user
    // moves; otherwise the next tick (≤1s) picks the new stamp up.
    if (wasWarning) evaluate();
  }

  function onStorage(event: StorageEvent): void {
    // Fires only in the OTHER tabs, which is exactly the point: another tab
    // reporting activity should clear this tab's warning without waiting a tick.
    if (event.key !== null && event.key !== IDLE_ACTIVITY_KEY) return;
    evaluate();
  }

  function onVisibilityChange(): void {
    // A background tab's ticker is throttled to about one firing a minute, so a
    // tab the user returns to could otherwise sit signed-in for up to a minute
    // past the window. Re-derive the moment it is on screen. Deliberately NOT
    // treated as activity: returning to a tab because another app closed is not
    // engagement with this one.
    evaluate();
  }

  function listen(): void {
    if (typeof window === 'undefined') return;
    for (const type of ACTIVITY_EVENTS) window.addEventListener(type, onActivity, LISTENER_OPTIONS);
    window.addEventListener('storage', onStorage);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
  }

  function unlisten(): void {
    if (typeof window === 'undefined') return;
    for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, onActivity, LISTENER_OPTIONS);
    window.removeEventListener('storage', onStorage);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  }

  function teardown(): void {
    if (!running) return;
    running = false;
    if (ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
    unlisten();
  }

  function start(): void {
    if (running) return;
    running = true;
    const now = Date.now();
    // A cross-origin session handoff just landed (see markSessionHandoffArrival):
    // the session arriving is live, so its window starts now. Checked FIRST, and
    // deliberately not folded into the seed below — the whole point is that it
    // overrides a stamp this origin already holds.
    if (consumeSessionHandoffArrival()) {
      localLastActivity = now;
      writeStamp(now);
      lastPublishedAt = now;
      // A session that just arrived owes no explanation for the previous one's end,
      // and is not itself somebody's sign-out.
      clearSignOutReason();
      clearDeliberateSignOut();
    } else if (readStamp(now) === null) {
      // Seed only when nothing is known. Seeding unconditionally would let a new
      // tab reset a window that a sibling tab is already most of the way through.
      writeStamp(now);
      lastPublishedAt = now;
    }
    listen();
    ticker = setInterval(evaluate, TICK_MS);
    // Evaluate straight away: an app booting with an already-expired stamp (a
    // tab restored hours later, a session left open overnight) must sign out on
    // arrival, not one tick into rendering someone's data.
    evaluate();
  }

  function staySignedIn(): void {
    if (fired) return;
    // Publish BEFORE re-deriving: `publishActivity` skips its throttle only while
    // the warning is up, so clearing the flag first would be exactly the case
    // where a sibling tab needs the news immediately and does not get it.
    // `evaluate()` then lowers the warning from the fresh stamp — no flag is set
    // by hand, so the dialog's state and the timer's cannot drift apart.
    publishActivity(Date.now());
    evaluate();
  }

  const stopWatch = watch(
    () => options.isAuthenticated(),
    (authenticated, previously) => {
      if (!authenticated) {
        teardown();
        return;
      }
      // `previously === false` is a sign-in that happened with this app already
      // mounted: start that session's window now, so a stale stamp from an
      // earlier session in this browser cannot end it immediately.
      //
      // `previously === undefined` is the watcher's own first run — the app
      // booted authenticated, and the stored stamp (however old) IS the truth
      // about when this session was last used. Except when this page load IS the
      // arrival of a handed-over session: that case is a sign-in too, and `start()`
      // below reseeds for it. It cannot be handled here, because the app boots with
      // the PREVIOUS session's token still in storage, so `authenticated` is already
      // true on this first run and `previously` is never `false`.
      if (previously === false) {
        const now = Date.now();
        fired = false;
        localLastActivity = now;
        writeStamp(now);
        lastPublishedAt = now;
        // A fresh session owes no explanation for the previous one's end, and is
        // not itself somebody's sign-out.
        clearSignOutReason();
        clearDeliberateSignOut();
      }
      start();
    },
    { immediate: true },
  );

  function stop(): void {
    stopWatch();
    teardown();
  }

  onScopeDispose(stop);

  return { warningVisible, secondsRemaining, staySignedIn, stop };
}

/** `123` → `2:03`. Grammar-free countdown for the warning dialog. */
export function formatIdleCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
